import ButtonWithTooltip from "@/_components/ButtonWithTooltip"
import CustomButton from "@/_components/CustomButton"
import WorkflowCommentModal from "@/_components/WorkflowCommentModal"
import { getUriAndCacheResponse } from "@/_services/cache"
import { copyEntities, pasteEntities } from "@/_services/copyPaste"
import { loc } from "@/_services/localization"
import { addNotification, addOops } from "@/_services/notification"
import { labelFromName, onDropdownMenuButtonKeyDown, onDropdownToggleKeyDown } from "@/_services/utils"
import axios from "axios"
import { getEntityServerRoute, isObject, lowerCaseFirstLetter, toPlural } from "basikon-common-utils"
import React from "react"
import { Col, Dropdown, Row } from "react-bootstrap"
import { withRouter } from "react-router-dom"

class EntitiesButtons extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      transitionLoading: false,
      commentModalTransition: null,
    }
  }

  handleTransitionClick = transition => {
    if (transition.showCommentModal) return this.setState({ commentModalTransition: transition })
    this.executeTransition(transition)
  }

  handleCommentModalClose = reasons => {
    const { commentModalTransition } = this.state
    this.setState({ commentModalTransition: null }, () => {
      if (reasons) this.executeTransition(commentModalTransition, reasons)
    })
  }

  executeTransition = async (transition, reasons) => {
    const { selectedEntities, entityName, getEntities } = this.props

    this.setState({ transitionLoading: true })

    const serverRoute = getEntityServerRoute(entityName)
    const transitions = Array.from(selectedEntities).map(registration => {
      const url = `${serverRoute}/${registration}/transitions/${transition.code}`
      return (reasons ? axios.post(url, { reasons }) : axios.get(url))
        .then(() =>
          addNotification(
            `Transition "${transition.label || transition.code}" executed successfully for ${entityName.toLowerCase()} "${registration}"`,
          ),
        )
        .catch(error => addOops(error))
    })

    await Promise.all(transitions)
    await getEntities()

    this.setState({ transitionLoading: false })
  }

  handleButtonClick = async ({ url, redirectTo, newTabUrl, successMessage }) => {
    const { history, selectedEntities } = this.props

    if (!url) return addOops("Missing 'url' in page button props")

    const entityRegistrations = Array.from(selectedEntities)
    try {
      const result = (await axios.post(url, { entityRegistrations })).data
      if (isObject(result)) {
        if (result.successMessage || successMessage) addNotification(result.successMessage || successMessage)

        const _redirectTo = result.redirectTo || redirectTo
        if (_redirectTo) _redirectTo.startsWith("http") ? (window.location.href = _redirectTo) : history.push(_redirectTo)
        else if (result.newTabUrl || newTabUrl) window.open(result.newTabUrl || newTabUrl, "_blank")
      }
    } catch (error) {
      addOops(error)
    }
  }

  handleCopy = () => {
    const { entityName, entities, selectedEntities } = this.props

    const entitiesToCopy = entities.filter(entity => selectedEntities.has(entity.registration))
    copyEntities(entitiesToCopy)

    const _entityName = entitiesToCopy.length === 1 ? labelFromName(entityName) : labelFromName(toPlural(entityName))
    addNotification(`${entitiesToCopy.length} ${lowerCaseFirstLetter(_entityName)} copied to clipboard`)
  }

  handlePaste = async () => {
    const { entityName, entities, handleSetState } = this.props
    const serverRoute = getEntityServerRoute(entityName)
    const { schema } = await getUriAndCacheResponse(`${serverRoute}-schema`)

    try {
      const pastedEntities = (await axios.post(serverRoute, await pasteEntities(schema))).data
      handleSetState({ entities: [...pastedEntities.reverse(), ...entities], selectedEntities: new Set() })

      const _entityName = pastedEntities.length === 1 ? labelFromName(entityName) : labelFromName(toPlural(entityName))
      addNotification(`${pastedEntities.length} ${lowerCaseFirstLetter(_entityName)} pasted successfully`)
    } catch (error) {
      console.log("Paste error is", error)
      return addNotification("Invalid data", "warning")
    }
  }

  render() {
    const { transitionLoading, commentModalTransition } = this.state
    const {
      entities,
      transitions = [],
      selectedEntities,
      customButtons = [],
      transitionsLoading,
      handleSelectAll,
      dropdownLabel = loc("Workflow transitions"),
      transitionsButton,
      showCopyPasteButton,
    } = this.props
    const loading = transitionLoading || transitionsLoading || !!commentModalTransition
    const allEntitiesSelected = selectedEntities.size === entities.length
    const availableTransitions = transitions.filter(transition => !transition.hidden && !transitionsButton?.[`props_${transition.code}`]?.hidden)

    return (
      <Row className="entity-transitions-button">
        <Col xs={12} className="flex align-items-center justify-content-end">
          {showCopyPasteButton && selectedEntities.size > 0 && (
            <>
              <ButtonWithTooltip
                pullRight
                bsSize="xs"
                simple={false}
                bsStyle="primary"
                onClick={this.handleCopy}
                btnClassName="flex-center"
                className="icn-copy icn-sm"
              />
              <ButtonWithTooltip
                pullRight
                bsSize="xs"
                simple={false}
                bsStyle="primary"
                onClick={this.handlePaste}
                btnClassName="flex-center mr-5px"
                className="icn-paste icn-sm"
              />
            </>
          )}

          {!transitionsButton?.hidden && selectedEntities.size > 0 && handleSelectAll && (
            <CustomButton
              tabIndex="0"
              bsStyle="info"
              bsSize="small"
              className="mt-0 mb-0"
              fill
              onClick={() => handleSelectAll(allEntitiesSelected ? new Set() : new Set(entities.map(e => e.registration)))}
            >
              {` ${loc(allEntitiesSelected ? "Deselect all" : "Select all")}`}
            </CustomButton>
          )}

          {(customButtons || []).map((button, index) => {
            const { fill = true, bsSize = "small", bsStyle = "info", iconClassName, label } = button

            return (
              <CustomButton
                key={index}
                tabIndex="-1"
                fill={fill}
                bsSize={bsSize}
                bsStyle={bsStyle}
                iconClassName={iconClassName}
                onClick={() => this.handleButtonClick(button)}
              >
                {label || loc`Missing label`}
              </CustomButton>
            )
          })}

          {!transitionsButton?.hidden && availableTransitions.length > 0 && (
            <Dropdown
              bsSize="small"
              bsStyle="primary btn-fill"
              className="inline-flex-center mt-0 mb-0"
              disabled={loading}
              onClick={event => {
                const { target } = event
                target.focus()
                target.nextSibling?.click()
              }}
            >
              <CustomButton bsStyle="info" className="grow" tabIndex="-1" label={dropdownLabel} />
              <Dropdown.Toggle bsStyle="info" onKeyDown={onDropdownToggleKeyDown} aria-label={dropdownLabel} />
              <Dropdown.Menu className="br-theme overflow-hidden border-0">
                {availableTransitions.map(transition => (
                  <CustomButton
                    key={transition.code}
                    tabIndex="0"
                    bsStyle="info"
                    bsSize="small"
                    fill
                    className="btn-simple white-space-normal w-100 text-left br-0 m-0 inline-flex align-items-center outline-accessible-inset"
                    onClick={event => {
                      event.stopPropagation()
                      this.handleTransitionClick(transition)
                    }}
                    onKeyDown={onDropdownMenuButtonKeyDown}
                  >
                    {transition.icon && <i className={"icn-xs mr-5px " + transition.icon} />}
                    {` ${loc(transition.label || transition.code)}`}
                  </CustomButton>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          )}

          {commentModalTransition && (
            <WorkflowCommentModal
              transition={commentModalTransition}
              entity={{ entityRegistrations: Array.from(selectedEntities) }}
              onClose={this.handleCommentModalClose}
            />
          )}
        </Col>
      </Row>
    )
  }
}

export default withRouter(EntitiesButtons)
